import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { payrollApi, Employee, Payslip, CreateEmployeeData, UpdateEmployeeData, GeneratePayslipData, PayrollUser, AddUserToPayrollData } from '../services/payroll';
import { formatCurrency } from '../utils/currency';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { CURRENCIES } from '../utils/currency';
import { useCompany } from '../contexts/CompanyContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Payroll: React.FC = () => {
  const { baseCurrency } = useCompany();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<PayrollUser[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [payslipDialogOpen, setPayslipDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [generatingPayslip, setGeneratingPayslip] = useState(false);

  const employeeForm = useForm<CreateEmployeeData>({
    defaultValues: {
      name: '',
      email: '',
      salary: 0,
      remunerationValue: 0,
      currency: baseCurrency,
      paymentDate: 1,
      contractType: 'Salaried',
      payFrequency: 'monthly',
      payType: 'salary',
      marginPercentage: undefined,
      taxCode: '',
      taxId: '',
      socialSecurityNumber: '',
      jurisdiction: '',
      phone: '',
      employmentStartDate: '',
    },
  });

  const addUserForm = useForm<AddUserToPayrollData>({
    defaultValues: {
      userId: '',
      salary: 0,
      currency: baseCurrency,
      paymentDate: 1,
    },
  });

  const payslipForm = useForm<GeneratePayslipData>({
    defaultValues: {
      employeeId: '',
      payPeriodStart: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      payPeriodEnd: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    },
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersData, employeesData, payslipsData] = await Promise.all([
        payrollApi.getUsers(),
        payrollApi.getEmployees(),
        payrollApi.getPayslips(),
      ]);
      setUsers(usersData);
      setEmployees(employeesData);
      setPayslips(payslipsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenEmployeeDialog = (employee?: Employee) => {
    if (employee) {
      setEditingEmployee(employee);
      employeeForm.reset({
        name: employee.name,
        email: employee.email,
        salary: employee.salary,
        remunerationValue: employee.remunerationValue || employee.salary,
        currency: employee.currency,
        paymentDate: employee.paymentDate,
        contractType: employee.contractType || 'Salaried',
        payFrequency: employee.payFrequency || 'monthly',
        payType: employee.payType || 'salary',
        marginPercentage: employee.marginPercentage,
        taxCode: employee.taxCode || '',
        taxId: employee.taxId || '',
        socialSecurityNumber: employee.socialSecurityNumber || '',
        jurisdiction: employee.jurisdiction || '',
        phone: employee.phone || '',
        employmentStartDate: employee.employmentStartDate || '',
      });
    } else {
      setEditingEmployee(null);
      employeeForm.reset();
    }
    setEmployeeDialogOpen(true);
  };

  const handleCloseEmployeeDialog = () => {
    setEmployeeDialogOpen(false);
    setEditingEmployee(null);
    employeeForm.reset();
  };

  const handleOpenAddUserDialog = () => {
    addUserForm.reset({
      userId: '',
      salary: 0,
      currency: baseCurrency,
      paymentDate: 1,
    });
    setAddUserDialogOpen(true);
  };

  const handleCloseAddUserDialog = () => {
    setAddUserDialogOpen(false);
    addUserForm.reset();
  };

  const handleUserSelectionChange = (userId: string) => {
    const selectedUser = users.find(u => u.id === userId);
    if (selectedUser && selectedUser.payRateDay) {
      // Auto-calculate monthly salary: pay_rate_day * 20 working days
      const monthlySalary = selectedUser.payRateDay * 20;
      addUserForm.setValue('salary', monthlySalary);
    }
  };

  const handleAddUserSubmit = async (data: AddUserToPayrollData) => {
    try {
      setError(null);
      await payrollApi.addUserToPayroll(data);
      handleCloseAddUserDialog();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add user to payroll');
    }
  };

  const handleEmployeeSubmit = async (data: CreateEmployeeData) => {
    try {
      setError(null);
      // Ensure salary is set from remunerationValue if not provided (backward compatibility)
      const submitData: any = {
        ...data,
        salary: data.remunerationValue || data.salary || 0,
        remunerationValue: data.remunerationValue || data.salary || 0,
      };

      // Remove empty strings and convert to null for optional fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });
      
      if (editingEmployee) {
        console.log('Updating employee:', editingEmployee.id, submitData);
        await payrollApi.updateEmployee(editingEmployee.id, submitData);
        handleCloseEmployeeDialog();
        loadData();
      } else {
        await payrollApi.createEmployee(submitData);
        handleCloseEmployeeDialog();
        loadData();
      }
    } catch (err: any) {
      console.error('Error saving employee:', err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors?.[0]?.msg || 
                          err.message || 
                          'Failed to save employee';
      setError(errorMessage);
    }
  };

  const handleOpenPayslipDialog = () => {
    payslipForm.reset({
      employeeId: '',
      payPeriodStart: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      payPeriodEnd: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    });
    setPayslipDialogOpen(true);
  };

  const handleClosePayslipDialog = () => {
    setPayslipDialogOpen(false);
    payslipForm.reset();
  };

  const handleGeneratePayslip = async (data: GeneratePayslipData) => {
    try {
      setError(null);
      setGeneratingPayslip(true);
      await payrollApi.generatePayslip(data);
      handleClosePayslipDialog();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate payslip');
    } finally {
      setGeneratingPayslip(false);
    }
  };

  const handleDownloadPDF = async (payslipId: string) => {
    try {
      setError(null);
      const blob = await payrollApi.downloadPayslipPDF(payslipId);
      
      // Check if the response is actually a PDF (not an error JSON)
      if (blob.type && blob.type !== 'application/pdf') {
        // Try to read as text to get error message
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          setError(errorData.message || 'Failed to download PDF');
          return;
        } catch {
          setError('Failed to download PDF: Invalid response format');
          return;
        }
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip-${payslipId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('PDF download error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to download PDF';
      setError(errorMessage);
    }
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee?.name || 'Unknown';
  };

  const getUsersNotInPayroll = () => {
    const employeeEmails = new Set(employees.map(e => e.email.toLowerCase()));
    return users.filter(u => !employeeEmails.has(u.email.toLowerCase()));
  };

  if (loading && employees.length === 0 && payslips.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Payroll</Typography>
        {tabValue === 0 && (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddUserDialog}
            >
              Add User to Payroll
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenEmployeeDialog()}
            >
              Add Manual Employee
            </Button>
          </Box>
        )}
        {tabValue === 1 && (
          <Button
            variant="contained"
            startIcon={<ReceiptIcon />}
            onClick={handleOpenPayslipDialog}
            disabled={employees.length === 0}
          >
            Generate Payslip
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Employees" />
          <Tab label="Payslips" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Contract Type</TableCell>
                  <TableCell>Salary/Rate</TableCell>
                  <TableCell>Pay Frequency</TableCell>
                  <TableCell>Payment Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary">No employees found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((employee) => (
                    <TableRow key={employee.id}>
                      <TableCell>{employee.name}</TableCell>
                      <TableCell>{employee.email}</TableCell>
                      <TableCell>
                        <Chip 
                          label={employee.contractType || 'Salaried'} 
                          size="small" 
                          color={
                            employee.contractType === 'Day Rate Associate' ? 'secondary' :
                            employee.contractType === 'Day Rate Employee' ? 'primary' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {formatCurrency(
                          employee.remunerationValue || employee.salary, 
                          employee.currency
                        )}
                        {(employee.contractType === 'Day Rate Employee' || 
                          employee.contractType === 'Day Rate Associate') && 
                          `/day`}
                      </TableCell>
                      <TableCell>
                        {(employee.payFrequency || 'monthly').charAt(0).toUpperCase() + 
                         (employee.payFrequency || 'monthly').slice(1)}
                      </TableCell>
                      <TableCell>{employee.paymentDate}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEmployeeDialog(employee)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Pay Period</TableCell>
                  <TableCell>Gross Salary</TableCell>
                  <TableCell>Deductions</TableCell>
                  <TableCell>Net Salary</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payslips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">No payslips found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payslips.map((payslip) => (
                    <TableRow key={payslip.id}>
                      <TableCell>{getEmployeeName(payslip.employeeId)}</TableCell>
                      <TableCell>
                        {format(parseISO(payslip.payPeriodStart), 'MMM d')} -{' '}
                        {format(parseISO(payslip.payPeriodEnd), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(payslip.grossSalary, payslip.currency)}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(
                          payslip.incomeTax + payslip.socialSecurity,
                          payslip.currency
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(payslip.netSalary, payslip.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/payroll/payslips/${payslip.id}`)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadPDF(payslip.id)}
                            >
                              <PictureAsPdfIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Employee Dialog */}
      <Dialog open={employeeDialogOpen} onClose={handleCloseEmployeeDialog} maxWidth="sm" fullWidth>
        <form onSubmit={employeeForm.handleSubmit(handleEmployeeSubmit)}>
          <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Name"
                required
                {...employeeForm.register('name', { required: 'Name is required' })}
                error={!!employeeForm.formState.errors.name}
                helperText={employeeForm.formState.errors.name?.message}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                required
                {...employeeForm.register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={!!employeeForm.formState.errors.email}
                helperText={employeeForm.formState.errors.email?.message}
              />
              <Controller
                name="contractType"
                control={employeeForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Contract Type</InputLabel>
                    <Select {...field} label="Contract Type">
                      <MenuItem value="Salaried">Salaried</MenuItem>
                      <MenuItem value="Day Rate Employee">Day Rate Employee</MenuItem>
                      <MenuItem value="Day Rate Associate">Day Rate Associate</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label={
                      employeeForm.watch('contractType') === 'Day Rate Employee' || 
                      employeeForm.watch('contractType') === 'Day Rate Associate'
                        ? 'Daily Rate'
                        : 'Monthly Salary'
                    }
                    type="number"
                    required
                    {...employeeForm.register('remunerationValue', {
                      required: 'Remuneration value is required',
                      valueAsNumber: true,
                      min: { value: 0, message: 'Must be positive' },
                    })}
                    error={!!employeeForm.formState.errors.remunerationValue}
                    helperText={employeeForm.formState.errors.remunerationValue?.message}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="currency"
                    control={employeeForm.control}
                    rules={{ required: 'Currency is required' }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth required error={!!fieldState.error}>
                        <InputLabel>Currency</InputLabel>
                        <Select {...field} label="Currency">
                          {CURRENCIES.map((currency) => (
                            <MenuItem key={currency.code} value={currency.code}>
                              {currency.code} - {currency.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
              {employeeForm.watch('contractType') === 'Day Rate Associate' && (
                <TextField
                  fullWidth
                  label="Margin Percentage"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  {...employeeForm.register('marginPercentage', {
                    valueAsNumber: true,
                    min: { value: 0, message: 'Must be between 0 and 100' },
                    max: { value: 100, message: 'Must be between 0 and 100' },
                  })}
                  error={!!employeeForm.formState.errors.marginPercentage}
                  helperText={employeeForm.formState.errors.marginPercentage?.message || 'Margin percentage for Day Rate Associate (0-100%)'}
                />
              )}
              <Controller
                name="payFrequency"
                control={employeeForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Pay Frequency</InputLabel>
                    <Select {...field} label="Pay Frequency">
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="bi-weekly">Bi-Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <TextField
                fullWidth
                label="Payment Date (Day of Month)"
                type="number"
                required
                inputProps={{ min: 1, max: 31 }}
                {...employeeForm.register('paymentDate', {
                  required: 'Payment date is required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Must be between 1 and 31' },
                  max: { value: 31, message: 'Must be between 1 and 31' },
                })}
                error={!!employeeForm.formState.errors.paymentDate}
                helperText={employeeForm.formState.errors.paymentDate?.message}
              />
              <TextField
                fullWidth
                label="Employment Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...employeeForm.register('employmentStartDate')}
              />
              <TextField
                fullWidth
                label="Phone"
                {...employeeForm.register('phone')}
              />
              <TextField
                fullWidth
                label="Tax Code"
                {...employeeForm.register('taxCode')}
              />
              <TextField
                fullWidth
                label="Tax ID"
                {...employeeForm.register('taxId')}
              />
              <TextField
                fullWidth
                label="Social Security Number"
                {...employeeForm.register('socialSecurityNumber')}
              />
              <TextField
                fullWidth
                label="Jurisdiction"
                {...employeeForm.register('jurisdiction')}
                helperText="Tax jurisdiction (e.g., 'US', 'UK', 'default')"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEmployeeDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingEmployee ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Add User to Payroll Dialog */}
      <Dialog open={addUserDialogOpen} onClose={handleCloseAddUserDialog} maxWidth="sm" fullWidth>
        <form onSubmit={addUserForm.handleSubmit(handleAddUserSubmit)}>
          <DialogTitle>Add User to Payroll</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Controller
                name="userId"
                control={addUserForm.control}
                rules={{ required: 'User is required' }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth required error={!!fieldState.error}>
                    <InputLabel>User</InputLabel>
                    <Select 
                      {...field} 
                      label="User"
                      onChange={(e) => {
                        field.onChange(e);
                        handleUserSelectionChange(e.target.value);
                      }}
                    >
                      {getUsersNotInPayroll().map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.name} ({user.email})
                          {user.payRateDay && ` - ${formatCurrency(user.payRateDay, 'USD')}/day`}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {fieldState.error.message}
                      </Typography>
                    )}
                    {getUsersNotInPayroll().length === 0 && (
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 1.75 }}>
                        All users are already in payroll
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Monthly Salary"
                    type="number"
                    required
                    {...addUserForm.register('salary', {
                      required: 'Salary is required',
                      valueAsNumber: true,
                      min: { value: 0, message: 'Salary must be positive' },
                    })}
                    error={!!addUserForm.formState.errors.salary}
                    helperText={addUserForm.formState.errors.salary?.message || 'Auto-calculated from daily rate if available'}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="currency"
                    control={addUserForm.control}
                    rules={{ required: 'Currency is required' }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth required error={!!fieldState.error}>
                        <InputLabel>Currency</InputLabel>
                        <Select {...field} label="Currency">
                          {CURRENCIES.map((currency) => (
                            <MenuItem key={currency.code} value={currency.code}>
                              {currency.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="Payment Date (Day of Month)"
                type="number"
                required
                inputProps={{ min: 1, max: 31 }}
                {...addUserForm.register('paymentDate', {
                  required: 'Payment date is required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Must be between 1 and 31' },
                  max: { value: 31, message: 'Must be between 1 and 31' },
                })}
                error={!!addUserForm.formState.errors.paymentDate}
                helperText={addUserForm.formState.errors.paymentDate?.message}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddUserDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={getUsersNotInPayroll().length === 0}>
              Add to Payroll
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Payslip Generation Dialog */}
      <Dialog open={payslipDialogOpen} onClose={handleClosePayslipDialog} maxWidth="sm" fullWidth>
        <form onSubmit={payslipForm.handleSubmit(handleGeneratePayslip)}>
          <DialogTitle>Generate Payslip</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Controller
                name="employeeId"
                control={payslipForm.control}
                rules={{ required: 'Employee is required' }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth required error={!!fieldState.error}>
                    <InputLabel>Employee</InputLabel>
                    <Select {...field} label="Employee">
                      {employees.map((employee) => (
                        <MenuItem key={employee.id} value={employee.id}>
                          {employee.name} ({formatCurrency(employee.salary, employee.currency)})
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
              <TextField
                fullWidth
                label="Pay Period Start"
                type="date"
                required
                InputLabelProps={{ shrink: true }}
                {...payslipForm.register('payPeriodStart', {
                  required: 'Start date is required',
                })}
                error={!!payslipForm.formState.errors.payPeriodStart}
                helperText={payslipForm.formState.errors.payPeriodStart?.message}
              />
              <TextField
                fullWidth
                label="Pay Period End"
                type="date"
                required
                InputLabelProps={{ shrink: true }}
                {...payslipForm.register('payPeriodEnd', {
                  required: 'End date is required',
                })}
                error={!!payslipForm.formState.errors.payPeriodEnd}
                helperText={payslipForm.formState.errors.payPeriodEnd?.message}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePayslipDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={generatingPayslip}>
              {generatingPayslip ? 'Generating...' : 'Generate Payslip'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Payroll;
